import { OlpLabelParams } from "../types";
import { formatInvisibles, getReferenceCode } from "./trackedLabelFormat";

const RETURN = "\\&";

/**
 * Method to convert Labels2Go OLP to zpl format to print label
 * This template is designed as per 12 dpmm (300dpi)
 * @param params : OLPLabelParams
 * @returns zpl string
 */
export const convertLabels2GoOlpQrCode = (params: OlpLabelParams) => {
  const { qrcode, serviceName1, serviceName2, serviceName3, templateId, destinationCountry } = params;
  const fields = getOlpLabelFields(qrcode);

  const fullLabel = `^XA

^FX --- Generate border line ---
^FO78,84^GB1044,1632,3^FS

^FX --- Top section with service name and royal mail logo ---

${getServiceName(serviceName1, serviceName2, serviceName3, templateId)}

${getExpressLogo(destinationCountry, fields["|QRCode="])}

${getRLogo(destinationCountry, fields["|QRCode="])}

${getRMLogo(destinationCountry)}

^FX --- Generate line separtor ---
^FO78,372^GB1044,3,3^FS

^FX --- Air Mail Logo ---
${getAirMailLogo(destinationCountry)}

^FX --- Second section with service name and weight ---
^FO978,372^GB144,54,3^FS
${getPipFormat(fields["|QRCode="])}
^FO978,426^GB144,54,3^FS
^FO978,440^FB144,1,0,C^CF0,34^FD${getWeight(fields["|Weight="], fields["|WeightUnit="])}^FS

^FX --- Generate line separtor ---
^FO78,480^GB1044,3,3^FS

^FX --- Third section ---
^CF0,34
^FO138,516^FD${getReferenceCode(fields["|QRCode="])}^FS

^FX --- Data Matrix bar code ---
^FO138,586^BXN,8,200^FD${formatInvisibles(fields["|QRCode="])}^FS

^FX -- Bar code ---
${getOneDBarcode(fields["|1DBarcode="])}
${getBarcode(fields["|1DBarcode="])}

^FX --- Generate line separtor ---
^FO78,900^GB1044,3,3^FS

^FX --- Fourth section ---

^FX -- Addess box ---
^CF0,46
^FO102,936^FB740,10^FD${getSenderDetailAddress(fields["|SendersDetails="])}^FS

^FX --- Generate line separtor ---
^FO78,1284^GB1044,3,3^FS

^FX --- Generate line separtor ---
^FO78,1392^GB1044,3,3^FS

^FX --- Last section ---
^CF0,34
^FO78,1416^FB1020,1,0,R^FDPostage Cost^FS
^FO78,1462^FB1020,1,0,R^CI27^FH_^FD_A3${fields["|Price="]}^FS
^FO78,1520^FB1020,1,0,R^FDPost by the end of^FS
^FO78,1566^FB1020,1,0,R^FD${fields["|PostBy="]}^FS
^FO78,1624^FB1020,1,0,R^FDPaid and printed from^FS
^FO78,1670^FB1020,1,0,R^FDClick & Drop^FS

^FX -- Rotated Returns Addess box ---
^FWB
^CF0,34
^FO866,936^FB324,7,,L^FD${getReturnAddress(fields["|RetDetails="])}^FS

^XZ`;
  return fullLabel;
};

export const getOlpLabelFields = (qrString: string) => {
  const markers = [
    "|QRCode=",
    "|1DBarcode=",
    "|SendersDetails=",
    "|RetDetails=",
    "|Weight=",
    "|WeightUnit=",
    "|Price=",
    "|PostBy=",
    "|CN=",
    "|CNSenderName=",
    "|CNSenderDetails=",
    "|CNChecks=",
    "|Other=",
    "|NumberOfItems=",
    "|Details=",
    "|Quantities=",
    "|Weights=",
    "|Values=",
    "|TotalQuantity=",
    "|TotalWeight=",
    "|TotalValue=",
    "|HSTariff=",
    "|VAT=",
    "|DateOfPosting=",
    "|Signature=",
  ];

  let output: Record<string, string> = {};
  markers.forEach(function (marker) {
    const startIndex = qrString.indexOf(marker) + marker.length;
    if (startIndex != marker.length - 1) {
      const endIndex = qrString.indexOf("|", startIndex);
      output = {
        ...output,
        [marker]: qrString.substring(startIndex, endIndex),
      };
    } else {
      output = { ...output, [marker]: "" };
    }
  });
  return output;
};

const getServiceName_1 = (serviceName: string) => {
  if (serviceName.length < 10) {
    return `^CF0,126
    ^FO102,180^FB708,2,0,C^FD${serviceName}^FS`;
  } else if (serviceName.length > 10) {
    if (
      serviceName === "International Signed" ||
      serviceName === "Intl Tracked & Signed" ||
      serviceName === "International Tracked"
    ) {
      return `^CF0,101
    ^FO102,140^FB564,2,0,C^FD${serviceName}^FS`;
    } else {
      return `^CF0,101
    ^FO102,140^FB708,2,0,C^FD${serviceName}^FS`;
    }
  }
};

const getServiceName_1_2 = (serviceName1: string, serviceName2?: string) => {
  return `^CF0,126
    ^FO102,140^FB708,1,0,C^FD${serviceName1}^FS
    ^CF0,72
    ^FO102,266^FB708,1,0,C^FD${serviceName2}^FS`;
};

const getServiceName_1_3 = (serviceName1: string, serviceName3?: string) => {
  if (serviceName1.length < 10) {
    return `^CF0,126
    ^FO102,180^FB444,1,0,R^FD${serviceName1}^FS
    ^CF0,207
    ^FO558,140^FB252,1,0,C^FD${serviceName3}^FS`;
  } else if (serviceName1.length > 10) {
    return `^CF0,101
    ^FO102,140^FB396,2,0,C^FD${serviceName1}^FS
    ^CF0,177
    ^FO486,170^FB312,2,0,L^FD${serviceName3}^FS`;
  }
};

const getServiceName_1_2_3 = (serviceName1: string, serviceName2: string, serviceName3: string) => {
  if (serviceName2.length < 10) {
    return `^CF0,126
    ^FO102,140^FB480,1,0,C^FD${serviceName1}^FS
    ^CF0,72
    ^FO102,266^FB480,2,0,C^FD${serviceName2}^FS
    ^CF0,207
    ^FO582,140^FB228,2,0,C^FD${serviceName3}^FS`;
  } else if (serviceName2.length > 10) {
    return `^CF0,126
    ^FO102,140^FB480,1,0,C^FD${serviceName1}^FS
    ^CF0,72
    ^FO102,266^FB480,1,0,C^FD${serviceName2}^FS
    ^CF0,207
    ^FO582,140^FB228,1,0,C^FD${serviceName3}^FS`;
  }
};

export const getServiceName = (
  serviceName1: string,
  serviceName2: string,
  serviceName3: string,
  templateId: string
) => {
  switch (templateId) {
    case "Template_SN_1":
      return getServiceName_1(serviceName1);
    case "Template_SN_1_2":
      return getServiceName_1_2(serviceName1, serviceName2);
    case "Template_SN_1_3":
      return getServiceName_1_3(serviceName1, serviceName3);
    case "Template_SN_1_2_3":
      return getServiceName_1_2_3(serviceName1, serviceName2, serviceName3);
  }
};

export const getPipFormat = (qrCode: string) => {
  const pip = qrCode.substring(6, 8);
  switch (pip) {
    case "17":
      return `^FO978,386^FB144,1,0,C^CF0,27^FDLetter^FS`;
    case "14":
      return `^FO978,386^FB144,1,0,C^CF0,26^FDLarge Letter^FS`;
    case "15":
      return `^FO978,386^FB144,1,0,C^CF0,25^FDSmall Parcel^FS`;
    case "16":
      return `^FO978,390^FB144,1,0,C^CF0,22^FDMedium Parcel^FS`;
    default:
      return "";
  }
};

export const getWeight = (weight: string, weightUnit: string) => {
  if (weightUnit) {
    return weight + weightUnit.toLowerCase();
  }
  return weight;
};

export const getOneDBarcode = (oneDBarcode: string) => {
  if (oneDBarcode !== "") {
    return `^BY3,2,160^FO556,610^BCN,200,N,N,N,A^FD${oneDBarcode}^FS`;
  }
  return "";
};

export const getBarcode = (barcode: string) => {
  if (barcode) {
    return `^FO556,846^FB480,1,0,C^FD${
      barcode.substring(0, 2) +
      " " +
      barcode.substring(2, 6) +
      " " +
      barcode.substring(6, 10) +
      " " +
      barcode.substring(10, 13)
    }^FS`;
  } else {
    return "";
  }
};

export const getSenderDetailAddress = (address: string) => {
  let senderAddress = address;
  address.split(RETURN).forEach((element) => {
    const m = element.match(/[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/);
    if (m) {
      if (m.index && m.index > 0) {
        senderAddress = address.replace(
          element,
          element.slice(0, (m.index as number) - 1) + RETURN + element.slice(m.index)
        );
      }
    }
  });
  return senderAddress;
};

export const getReturnAddress = (address: string) => {
  let returnAddress = address;
  address.split(RETURN).forEach((element) => {
    const m = element.match(/[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/);
    if (m) {
      if (m.index && m.index > 0) {
        returnAddress = address.replace(
          element,
          element.slice(0, (m.index as number) - 1) + RETURN + element.slice(m.index)
        );
      }
    }
  });
  return "Return Address" + RETURN + returnAddress;
};

const getProductId = (qrCode: string) => {
  const productId = qrCode.substring(40, 44);
  return productId.trim();
};

export const getExpressLogo = (destinationCountry: string, qrCode: string) => {
  const productId = getProductId(qrCode);
  if (!["GB", "GBR", "JE", "JEY", "GG", "GGY"].includes(destinationCountry) && productId === "036") {
    return `^FO690,180^GFA,1560,1560,12,,::::::I03E01E01F00F807C03C,I0FC07C03F01F00F80F8,I0F807C07E03E01F00F,001F00F807E03C03E01F,003F01F80FC07C07E03E,003E01F00F80F80FC03C,007E01E01F81F00F807C,,::::007WF8,::::::007PF03KF8,007OFJ0JF8,007NF07FFC1IF8,007MFE7FFC2C7FF8,007MF98FE1F31FF8,007MF33F70399FF8,007LFE6FB8C264FF8,007LF8D684361A7F8,007LF3810018433F8,007LF7228CE09CBF8,007KFEF2480CE4EBF8,007KFIC4075F66BF8,007KFD8FC700F32BF8,007KF918F03AFD83F8,007KF312807A7CD7F8,007KF626BD7D3E6FF8,007KF44E3D7E9CC7F8,007JFE55E7D7E4993F8,007JFE91E7D3E4623F8,007JFCCBC7D3F2C69F8,007JFCC3C7D3E39E9F8,007JFCE3C7CB877E1F8,007JFD100608217F1F8,007JFD17003E1E3IF8,007JFD10FFC87EBIF8,007JFD14I05FE1IF8,007JFD1787E5FF1FBF8,007JFD178FF5FF5F1F8,007JFD178FF4FF4EDF8,007JFC9787F4FF411F8,007JFC97C7F2FFAEFF8,007JFE97C7F27F38FF8,007JFE77C7FA7873FF8,007KF77C7F94197FF8,007KF71C7F90E17FF8,007KF2CI01E0D3FF8,007KF90181187D39F8,007KFC9880CBFD24F8,007KFE9EBFEBFC0CF8,007KFE4E9FE3FD19F8,007LF6E1FF3FDE9F8,007LF234FF1E183F8,007LF9827F00107F8,007LFE73003C14FF8,007MF1F03E039FF8,007MFC9F02673FF8,007MFE6C02087FF8,007NF1E0171IF8,007NF83FF87IF8,007OF8003JF8,007WF8,::::::::::::007E001SF8,007EI0LFC3KF8,007EI0LFE1KF8,007E001MF8KF8,007E0FK040103C0FF8,007E0F8O0803F8,007EJ01O03F8,007E00403K02I07F8,007E006070E01K03F8,007E0FE070E01K03F8,007E0FE030E01I0C01F8,007E0FC030C010307C1F8,007EJ01I018J01F8,007EN01C00803F8,007LF09IF8FF1FF8,007LF0PF8,::::I07UF8,J0TFC,J01RFE,K07QFC,L0PFC,L03OF,M07MF8,N03KF8,O07IFC,P0FFE,P03FC,Q04,,::::::::^FS`;
  } else {
    return "";
  }
};

export const getRLogo = (destinationCountry: string, qrCode: string) => {
  const productId = getProductId(qrCode);
  if (
    !["GB", "GBR", "JE", "JEY", "GG", "GGY"].includes(destinationCountry) &&
    (productId === "034" || productId === "035")
  ) {
    return `^FO678,180^GFA,1785,1785,15,,L01NFC,L07PF,K01RF,K03RFC,K07SF,K07SFC,K07TF,K0UF8,K0UFC,K0UFE,K0VF,:K0VF8,K0VFC,:K0VFE,::K0WF,:K0KFK01LF,K0KFL07KF8,K0KFL01KF8,:K0KFM0KF8,K0KFM07JF8,:::K0KFM07JFC,::::K0KFM07JF8,::::K0KFM0KF8,:K0KFL01KF8,K0KFL03KF,K0KFL0LF,K0KFK07LF,K0WF,K0VFE,:K0VFC,K0VF8,:K0VF,K0UFE,K0UFC,K0UF8,K0UF,K0TFE,K0TF8,K0SFE,K0SFC,::K0SFE,K0KFI0KFE,K0KFI07KF,K0KFI03KF8,:K0KFI01KFC,:K0KFJ0KFE,:K0KFJ07KF,:K0KFJ03KF8,K0KFJ01KF8,K0KFJ01KFC,K0KFK0KFC,K0KFK0KFE,K0KFK07JFE,K0KFK07KF,K0KFK03KF,K0KFK03KF8,K0KFK01KF8,K0KFL0KFC,K0KFL0KFE,K0KFL07JFE,K0KFL07KF,K0KFL03KF,:K0KFL01KF8,:K0KFM0KFC,:K0KFM07JFC,:K0KFM03JFE,K0KFM03JFC,K0JFEM01JFC,:K07IFCN0JF8,:K03IF8N07IF,K01IFO03FFC,L07FCO01FF8,M0EQ03C,,:::007gJF,::::::,:^FS`;
  } else {
    return "";
  }
};

export const getRMLogo = (destinationCountry: string) => {
  if (["GB", "GBR", "JE", "JEY", "GG", "GGY"].includes(destinationCountry)) {
    return `^FO834,96^GFA,8580,8580,33,,::::::Q03FF8gQ0FFE,Q03FFEK018Y018I0IF8,Q0300FK0183X018I0C03C,Q030038J0183X018I0C00C,Q030018J018Y018I0C00E,Q030018J018Y018I0C006,Q03001CJ018Y018I0C006,Q03I0CJ018Y018I0C006,Q03I0C03F0183080181F8087C1F800F98I0C0061003,Q03I0C07F81830C0183FC1DFC3FC01FF8I0C00E1803,Q03I0C0C0C1830C0186060F8060603878I0C00C1803,Q03I0C1C0E1830E030E070700E0707018I0C03C1806,Q03I0C180618306030C030600C0306018I0IF00C06,Q03I0C180618306030C030600C0306018I0IF80C0E,Q03I0C180618307060C030600C0306018I0C01E0E0C,Q03I0C1FFE18303060IF0600IF06018I0C006060C,Q03I0C1FFE183030E0IF0600IF06018I0C007061C,Q03I0C1800183018C0CI0600CI06018I0C0030718,Q03I0C1800183018C0CI0600CI06018I0C0030318,Q030018180018301980CI0600CI06018I0C0030338,Q030018180018300D80CI0600CI06018I0C00301B,Q0300381C0018300F80EI0600EI07018I0C00601B,Q0300F00E0C18300F00706060070603878I0C00E01F,Q03FFE007FC0C3007003FE06003FE03FFCI0IFC00E,Q03FF8003F00E3006001F806001F800F8CI0IFI0E,hR0C,:hQ018,hQ038,hP03F,hP03C,,::::gG0gHFE,:gG0EgG06,::::::::gG0EP0FP06,gG0EP0EP06,gG0EO026CO06,gG0EO03FCO06,:gG0EO026CO06,gG0EP06P06,gG0EP0FP06,gG0EgG06,gG0EO0198O06,gG0EK0840039C0021K06,gG0EJ01CE0039C00738J06,gG0EJ01CE6K0CF38J06,gG0EI039CE7K0E73BCI06,gG0EI03C00733FCCE003CI06,gG0EI039FC77BFDEE7F3CI06,gG0EI083FF879F9E1FFCJ06,gG0E001CIFE3008CIFE38006,gG0E001CFC7FC36C3FE3F78006,gG0E001DF007FB6DFE00FB8006,gG0EI01EI0FF0FEI078I06,gG0EI03CJ0F0FJ038I06,gG0E003BCJ036CJ03DC006,gG0E007B8I03F6FCI03DC006,gG0E003B80E3FF0FFCE01DC006,gG0EI0387JF6JFE1CI06,gG0EI038KF6JFE1CI06,gG0E003B8KF0KF1DC006,gG0E003F8FE7FF0FFCFF3BC006,gG0E0039DFE3FF6FFC7F39C006,gG0EI01DFC1FF6FF87FB8I06,gG0EI01FFC1FF0FF83FF8I06,gG0E001EFFC1KF83FF7I06,gG0E001EFFC1FE07F87FF78006,gG0E001C0FE13F0FC87F07I06,gG0EJ01F003B0DC00FK06,gG0EJ01F003801C00F8J06,gG0EJ017003861C00D8J06,gG0EK03A1F861F0DCK06,gG0EJ023F7F909IFCCJ06,gG0EJ033JF1DIF8CJ06,gG0EJ031F1FF1FF0F8CJ06,gG0EK0FF0FF0FF0FEK06,gG0EK07C67E07C67CK06,gG0EM0F00600FM06,gG0EK0C3F81F01FC2K06,gG0EK0PFEK06,gG0EK07OFEK06,gG0EgG06,gG0EK070783FC1E0EK06,gG0EK0F0FC3FC3F0FK06,gG0EK0F0F83FC1F0FK06,gG0EgG06,gG0EK07OFEK06,gG0EJ01QF8J06,:::gG0EK07OFEK06,gG0EgG06,:::::::::gG0EgG07,iKF,:::::::::hWF1MF,JFJ0gJF89KF31JF88OFE0F89JF,JFJ01gIF89KF31JF18OFC0789JF,JFK0gIF89JFE28JF10OFC4789JF,JF100387gHF89JFE28IFE20OFC0789JF,JF1I043gHF89JFE24IFE24OFE0F89JF,JF1I023gHF89JFE047FFE447OF1F89JF,JF13FE11gHF89JFE427FFC447QF89JF,JF13FF89gHF89JFE423FFC007QF89JF,JF13FF88FFC00IF37FF9BIF8I0789JFC413FF8807IFCI03E4F89JF,JF13FFC8FFI03FF23FF91FFCJ0789JFC411FF8027FFEJ03C4789JF,JF13FFC0FE07C0FF23FF91FFK0789JFC081FF1027FF8J03C4789JF,JF13FFC0F830187F23FF91FE0IFE789JFC088FF0323FF07FFE3C4789JF,JF13FFC0F8C0043F23FF91FC2I02789JFC8C0FE2323FE1J03C4789JF,JF13FFC8F1I023F23FF91F8CI02789JFC8C47E2703FC6J03C4789JF,JF13FF88E21FE11F23FF91F183FE2789JF89E47C4713F883FF03C4789JF,JF13FF81E23FF08F23FF91F11FFE2789JF89E23C4F13F00IF03C4789JF,JF13FE11C47FF88F23FF91E23FFE2789JF81F23C8F13F11IF03C4789JF,JF1I023C0IFC4F23FF91E47FFE2789JF91F1388F91F23IF03C4789JF,JF120043C8IFE4723FF91C47FFE2789JF91F1191F81E23IF03C4789JF,JF13018789IFE4723FF91C4IFE2789JF11F8911F81E27IF03C4789JF,JF130C0F89IFE0723FF91C0IFE2789JF13F8803F89E07IF03C4789JF,JF13881F89IFE0723FF91C0IFE2789JF03FC023F89E07IF03C4789JF,JF13C47F89IFE0723FF91C0IFE2789JF03FC407FC8E07IF03C4789JF,JF13E23F89IFE4703FF11C0IFE2789JF23FE047FC8E07IF03C4789JF,JF13E23FC8IFE4703FF11C4IFE6789JF23FE24FFC0E27IF23C4789JF,JF13F11FC0IFC4F11FE31E47FFC6789IFE27FF08FFC0E23FFE23C4789JF,JF13F88FC47FF88F88FC31E07FFC6789IFE27FF19FFC4F03FFE23C4789JF,JF13F88FE43FF88F880051E23FF8A789IFE07FF91FFC4F11FFC43C4789JF,JF13FC47E21FE11FC60091F10FE12789IFE07FF81FFE47887F083C4789JF,JF13FE23F1I023FE18211F081022789IFE47FFC3FFE078408103C4789JF,JF13FE01F8C0043FF07811F840042789IFC47FFC3FFE07C200203C4789JF,JF13FF11F830187FF80011FC30182789IFC4IFC7FFE07E180C03C4789JF,JF13FF88FE0FC0IFE0191FE07C02789IFC4IFE7FFE27F03E003C4789JF,JF13FF847FI03LF91FFJ02789IFC0IFEIFE23F8J03C4789JF,JF93FFC47FC00IFB3FF13FFC00627C9IFC8NF23FE00313E4F89JF,XF11FF13hGF,XF11FE23hGF,XF88FC23hGF,XF880047hGF,XFC6008hHF,XFE1821hHF,YFI03hHF,YF8007hHF,YFE01hIF,iKF,::::::::gG0EgG06,::::::::::::::::::::::::::gG0gHFE,:,::::::J03FF8gL07FFV0FC01FFC,J03FFEgL07FFCO06J03FF01IF,J0300FgL0601EK0CI06J0F07818078,J03003O0CV06006K0CI06J0C01818018,J030038N0CV06007O06I01CI01801C,J030018N0CV06003O06I018I01800C,:J030018N0CV06003O06I03J01800C,J03001807C01F83FC0FC007CC03FJ0600303F00C03E6I03J01800C,J0300181FF07FE3FC3FF00FFC07F8I060030FFC0C07FEI03J01801C,J03003838386I0C010301C3C0C0CI06007040C0C0E1EI03J018018,J0300307018CI0CI018380C1C0EI06006I060C1C06I03J018078,J0300F0601CCI0CI018300C1806I0601EI060C1806I03J01FFE,J03FFE0600CCI0CI018300C1806I07FFCI060C1806I0303FC1IF,J03FF80600CEI0CI018300C1806I07FFJ060C1806I0303FC1803C,J03J0600C7C00C007F8300C1FFEI06J01FE0C1806I03I0C1800C,J03J0600C1FC0C01FF8300C1FFEI06J07FE0C1806I03I0C1800E,J03J0600C01E0C03018300C18K06J0C060C1806I03I0C18006,J03J0600C0070C06018300C18K06I018060C1806I03800C18006,J03J0600C0030C06018300C18K06I018060C1806I01800C18006,J03J0601C0030C06018380C18K06I018060C1806I01801C18006,J03J070180070C06018183C1CK06I018060C1C06J0C0181800C,J03J0383840E06030381FFC0E0CI06J0C0E0C0E1EJ0F0701801C,J03J01FF0FFC07E3FFC07CC07FCI06J0IF0C0IFJ07FE01IF8,J03K07C07F801E0F8EI0C03FJ06J03E38C03E3J01F801FFE,gL0C,:gK018,gI01038,gI01FF,gJ0FC,,::::^FS`;
  } else {
    return `^FO834,96^GFA,8580,8580,33,,:::::::::::::::::::::::::::::::::::::::::gG0gHFE,:gG0EgG0E,gG0EgG06,:::::::gG0EP0FP06,:gG0EO026CO06,gG0EO03FCO06,:gG0EO03ECO06,gG0EP0EP06,gG0EP0FP06,gG0EgG06,gG0EO0198O06,gG0EK0840039C0021K06,gG0EJ01CE0039C00F38J06,gG0EJ01CE6K0CF78J06,gG0EI039CE7K0E73BCI06,gG0EI03C00F33FCCE003CI06,gG0EI03CFC77BFDEE7F3CI06,gG0EJ03FF879F9E1FFD8I06,gG0E001C7FFE3061C7FFE38006,gG0E001CJFC36C3JF78006,gG0E001DF00FFB6DFF00FF8006,gG0EI01EI0FF6FFI078I06,gG0EI03CI01F0F8I078I06,gG0E003BCJ036CJ03DC006,gG0E007B8I03F6F8I03DC006,gG0E003B8043FF0FFC403DC006,gG0EI0387JF6JFE1C8006,gG0EI038KF6JFE1CI06,gG0E003B8KF6KF3DC006,gG0E003F8FF7FF0FFEFF3FC006,gG0E003BDFE3FF6FFC7F3BC006,gG0E0019DFC3FF6FF87FB98006,gG0EI01DFC1FF0FF83FF8I06,gG0E001IFC1KF83FF7I06,gG0E001EFFC1FE07F87FF78006,gG0E001E4FE17E0FE87F278006,gG0EI081F003F0DC01F02I06,gG0EJ01F003909C00F8J06,gG0EJ01F003861C00F8J06,gG0EK0381B8E1D01CK06,gG0EJ023F1F861F8FC4J06,gG0EJ033IFB1DIFCCJ06,gG0EJ033JF1JF8CJ06,gG0EK0FF0FF0FF0FEK06,gG0EK07E47E07E67EK06,gG0EK018E3C638F18K06,gG0EK081F80F01F81K06,gG0EK0PFEK06,:gG0EgG06,gG0EM02S06,gG0EK0F0FC3FC3F0FK06,:gG0EM03K0CM06,gG0EgG06,gG0EJ01QFK06,gG0EJ01QF8J06,gG0EJ03QF8J06,gG0EJ01QF8J06,gG0EK0QFK06,gG0EgG06,::::::::::iKF,::::::::::JF8007gJFD9KFB3JFDDPF1FD9JF,JF8I07gIF89KF31JF98OFE0F89JF,JF8I01gIF89KF29JF10OFC4789JF,JF9IF0gIF89JFE28JF04OFC4789JF,JF9I0C7gHF89JFE20IFE24OFE0F89JF,JF9I023gHF89JFE047FFE24PF0F89JF,JF93FC11gHF89JFE447FFC447QF89JF,JF93FF11gHF89JFE427FFC447QF89JF,JF93FF89IF83WF89JFE423FF8807QF89JF,JF93FFC8FFC007FF27FF93IFJ0789JFC413FF8827IF8I03E4F89JF,JF93FFC8FFI01FF23FF93FF8J0789JFC491FF9027FFCJ03E4789JF,JF93FFC0FC1FF0FF23FF93FF07FFC789JFC089FF1127FF83FFE3E4789JF,JF93FFC0F860187F23FF93FE1C002789JFC8C8FF2323FE0C0033E4789JF,JF93FFC8F080063F23FF93F86I02789JFC8C4FE2303FC3I013E4789JF,JF93FFC8F107C31F23FF93F88I02789JF89C47E0703FC4I013E4789JF,JF93FF89E21FF11F23FF93F10FFE2789JF89E07C4713F887FF13E4789JF,JF93FF11E47FF88F23FF93E21FFE2789JF89E23C0F13F10IF13E4789JF,JF93FE11C47FFC4F23FF93E23FFE2789JF81F2388F93F11IF13E4789JF,JF9I023C8IFC4F23FF93E47FFE2789JF91F1189F91F23IF13E4789JF,JF9200C7C8IFE4723FF93C4IFE2789JF91F9191F81E27IF13E4789JF,JF93070F89IFE4723FF93C4IFE2789JF13F8813F89E27IF13E4789JF,JF93881F89IFE2723FF93C0IFE2789JF13FC823F89E07IF13E4789JF,JF938C3F89IFE2723FF93C0IFE2789JF13FC427FC9E07IF13E4789JF,JF93C47F89IFE6723FF93C0IFE2789JF23FE447FC8E07IF13E4789JF,JF93E23FC8IFE4703FF13C4IFE2789JF23FE247FC8E27IF33E4789JF,JF93E11FC8IFE4711FF13C4IFE6789JF23FE20FFC0E27IF33E4789JF,JF93F11FC4IFC4F91FE33E47FFC6789IFE27FF18FFC4F23FFE33E4789JF,JF93F88FC47FF88F887853E23FF8E789IFE27FF19FFC4F11FFC73E4789JF,JF93FC47E23FF09FC400D3E21FF0A789IFE07FF81FFE4F10FF853E4789JF,JF93FC47E30FE11FC20193F10FE12789IFE47FF83FFE47887F093E4789JF,JF93FE23F180023FE1C613F880022789IFE47FFC3FFE47C400113E4789JF,JF93FF11F8400C7FF01013F860042789IFE4IFC7FFE07C200613E4789JF,JF93FF01FC3830IFC0013FC18302789IFC4IFE7FFE27E0C1813E4789JF,JF93FF88FE0781JF0393FF03802789IFC4IFE7FFE27F01C013E4789JF,JF93FFC47F8003LF93FF80022789IFC0NF23FC00113E4789JF,RFE01IF33FF13FFE00gHF007QF,XF11FF13hGF,XF91FE23hGF,XF887867hGF,XFC400C7hGF,XFC2018hHF,XFE1CE1hHF,YFI03hHF,YFC007hHF,gF03hIF,iKF,::::::::gG0EgG06,::::::::::::::::::::::::::gG0gHFE,::,:::::J03FF8gL07FFV0FC01FFC,J03FFEgL07FFCO06J03FF01IF,J0300FgL0601EK0CI06J0F07818078,J03003O0CV06006K0CI06J0C01818018,J030038N0CV06007O06I01CI01801C,J030018N0CV06003O06I018I01800C,:J030018N0CV06003O06I03J01800C,J03001807C01F83FC0FC007CC03FJ0600303F00C03E6I03J01800C,J0300181FF07FE3FC3FF00FFC07F8I060030FFC0C07FEI03J01801C,J03003838386I0C010301C3C0C0CI06007040C0C0E1EI03J018018,J0300307018CI0CI018380C1C0EI06006I060C1C06I03J018078,J0300F0601CCI0CI018300C1806I0601EI060C1806I03J01FFE,J03FFE0600CCI0CI018300C1806I07FFCI060C1806I0303FC1IF,J03FF80600CEI0CI018300C1806I07FFJ060C1806I0303FC1803C,J03J0600C7C00C007F8300C1FFEI06J01FE0C1806I03I0C1800C,J03J0600C1FC0C01FF8300C1FFEI06J07FE0C1806I03I0C1800E,J03J0600C01E0C03018300C18K06J0C060C1806I03I0C18006,J03J0600C0070C06018300C18K06I018060C1806I03800C18006,J03J0600C0030C06018300C18K06I018060C1806I01800C18006,J03J0601C0030C06018380C18K06I018060C1806I01801C18006,J03J070180070C06018183C1CK06I018060C1C06J0C0181800C,J03J0383840E06030381FFC0E0CI06J0C0E0C0E1EJ0F0701801C,J03J01FF0FFC07E3FFC07CC07FCI06J0IF0C0IFJ07FE01IF8,J03K07C07F801E0F8EI0C03FJ06J03E38C03E3J01F801FFE,gL0C,:gK018,gI01038,gI01FF,gJ0FC,,::::^FS`;
  }
};

export const getAirMailLogo = (destinationCountry: string) => {
  if (!["GB", "GBR", "JE", "JEY", "GG", "GGY"].includes(destinationCountry)) {
    return `^FO102,402^GFA,3431,3431,73,lM03FC,I01FFK07F001KFN03FEL07FCJ03FEK0FF001FCP0JFCK01FL07IFEP03EJ078L03C03CJ03IFCJ01FL078,I01FF8J0FF801KFEM07FFL0FFCJ03FFK0FF003FEP0KFK01FL07JF8O03EJ078L03803CI01KFJ01F8K078,I03FF8J0FF801LF8L07FFL0FFCJ03FFK0FF003FEP0KF8J03F8K07JFCO07FJ078L07803CI03FC03FCI01F8K078,I03FF8J0FF801LFCL07FF8J01FFCJ07FF8J0FF003FEP0EI0FCJ03F8K07I07EO07FJ038L07003CI07EI0FEI01FCK078,I03FFCJ0FF801LFEL07FF8J03FFCJ07FF8J0FF003FEP0EI03EJ03B8K07I01FO0778I03CL07003CI0F8I01FI01FEK078,I07FFCJ0FF801MFL07FFCJ03FFCJ07FF8J0FF003FEP0EI01FJ073CK07J0F8N0E78I03CL0E003C001EK0F8001FEK078,I07FFCJ0FF801MFL07FFEJ07FFCJ0IFCJ0FF003FEP0EJ0FJ071CK07J078N0E38I01CL0E003C003EK07C001EFK078,I07FFEJ0FF801FE00IFL07FFEJ07FFCJ0IFCJ0FF003FEP0EJ0FJ0F1EK07J078M01E38I01EK01E003C003CK03C001EFK078,I0IFEJ0FF801FE001FF8K07IFJ0IFCI01IFCJ0FF003FEP0EJ078I0E1EK07J03CM01C3CJ0EK01C00380078K01E001E78J078,I0IFEJ0FF801FEI0FF8K07IFJ0IFCI01IFEJ0FF003FEP0EJ078I0E0EK07J03CM03C1CJ0EK01C00380078K01E001E3CJ078,001FEFFJ0FF801FEI0FF8K07IF8001IFCI01FDFEJ0FF003FEP0EJ078001E0FK07J03CM03C1CJ0FK03C003800FL01E001E1EJ078,001FEFFJ0FF801FEI0FF8K07IF8003IFCI03FDFEJ0FF003FEP0EJ078001C0FK07J03CM0381EJ07K038003800FM0F001E1EJ078,001FEFFJ0FF801FEI0FF8K07IFC003IFCI03FCFFJ0FF003FEP0EJ078001C07K07J03CM0780EJ078J038003801EM0F001E0FJ078,003FC7F8I0FF801FEI0FF8K07IFE007IFCI03FCFFJ0FF003FEP0EJ078003807K07J03CM0780FJ078J07I03801EM0F001E0F8I078,003FC7F8I0FF801FEI0FF8K07IFE007FBFCI07F8FFJ0FF003FEP0EJ0780038078J07J03CM07007J038J07I03801EM07001E078I078,003FC7FCI0FF801FEI0FFL07FDFF00FFBFCI07F87F8I0FF003FEP0EJ0780078038J07J03CM0F007J038J0FI03801EM07801E03CI078,003FC3FCI0FF801FE001FFL07FCFF00FF3FCI07F87F8I0FF003FEP0EJ078007003CJ07J038M0E0078I03CJ0EI03801CM07801E01EI078,007F83FCI0FF801FE003FEL07FCFF81FF3FCI0FF07F8I0FF003FEP0EJ07I0F001CJ07J078L01E0078I01CI01EI03801CM07801E01EI078,007F83FCI0FF801FE007FEL07FC7F83FE3FCI0FF03FCI0FF003FEP0EJ0FI0F001CJ07J078L01C0038I01CI01EI03801CM07801E00FI078,00FF81FEI0FF801LFCL07FC7FC3FE3FCI0FF03FCI0FF003FEP0EJ0FI0E001EJ07J0FM01C003CI01EI01CI03803CM07801E00FI078,00FF01FEI0FF801LFM07FC3FE7FC3FC001FE03FCI0FF003FEP0EI01E001EI0EJ07J0FM03C003CJ0EI03CI03803CM07801E00780078,00FF01FEI0FF801KFEM07FC1FE7F83FC001FE03FEI0FF003FEP0EI01E001EI0EJ07I03EM038001CJ0FI038I03803CM07801E003C0078,01FE01FFI0FF801KFCM07FC1JF83FC003FE01FEI0FF003FEP0EI07C001CI0FJ07I07CM038001EJ07I038I03803CM07801E003E0078,01FE00FFI0FF801KFEM07FC0JF03FC003FC01FEI0FF003FEP0EI0F8003CI0FJ07I0F8M078001EJ07I078I03803CM07801E001E0078,01FE00FF800FF801LFM07FC0JF03FC003FC01FFI0FF003FEP0E003FI038I07J07JFN07J0EJ078007J03803CM07801EI0F0078,03FC007F800FF801LF8L07FC07FFE03FC003FC00FFI0FF003FEP0JFEI078I078I07IFCN07J0FJ03800FJ03803CM07801EI0F8078,03FC007F800FF801LF8L07FC07FFE03FC007F800FF800FF003FEP0JFCI07J038I07IFO0FJ07J03800EJ03801CM07801EI078078,03FC007FC00FF801FE01FFCL07FC03FFC03FC007F8007F800FF003FEP0IFEJ07J038I070078N0EJ078I03C00EJ03801CM07801EI03C078,07LFC00FF801FE007FCL07FC03FF803FC007LF800FF003FEP0EM0LFCI070038M01LF8I03C01EJ03801EM07001EI03E078,07LFC00FF801FE003FEL07FC01FF803FC00MFC00FF003FEP0EM0LFCI07001CM01LF8I01C01CJ03801EM07001EI01E078,07LFE00FF801FE003FEL07FC00FF003FC00MFC00FF003FEP0EM0LFEI07001EM03LF8I01E01CJ03801EM0F001EJ0F078,0MFE00FF801FE001FEL07FC00FF003FC00MFC00FF003FEP0EL01EJ01EI07I0FM03CJ03CJ0E03CJ03801EM0F001EJ0F078,0MFE00FF801FE001FFL07FC007E003FC01MFE00FF003FEP0EL01CK0EI07I0FM038J01CJ0E038J03801EM0F001EJ07878,1NF00FF801FE001FFL07FCL03FC01MFE00FF003FEP0EL01CK0FI07I078L078J01CJ0F038J03800FL01E001EJ03C78,1NF00FF801FEI0FF8K07FCL03FC01MFE00FF003FEP0EL038K0FI07I078L078J01EJ0707K03800FL01E001EJ03E78,1FEJ0FF00FF801FEI0FF8K07FCL03FC03FCI01FF00FF003FEP0EL038K07I07I03CL07L0EJ0787K0380078K01E001EJ01E78,3FEJ0FF80FF801FEI0FFCK07FCL03FC03FCJ0FF00FF003FEP0EL078K07I07I01EL0FL0FJ0787K038007CK03C001EK0F78,3FCJ07F80FF801FEI07FCK07FCL03FC07FCJ0FF80FF003FEP0EL07L038007J0FL0EL07J038EK038003CK078001EK0F78,3FCJ07FC0FF801FEI07FCK07FCL03FC07F8J0FF80FF003KFEL0EL0FL038007J0FL0EL07J03CEK038003EK0FI01EK07F8,3FCJ07FC0FF801FEI03FEK07FCL03FC07F8J07F80FF003LFL0EL0FL03C007J078J01CL078I03DEK038001FJ01FI01EK03F8,7FCJ03FC0FF801FEI03FEK07FCL03FC0FF8J07F80FF003LFL0EL0EL01C007J07CJ01CL078I01FCK038I0FCI03EI01EK03F8,7F8J03FE0FF801FEI01FFK07FCL03FC0FFK07FC0FF003LFL0EK01EL01E007J03EJ03CL038I01FCK038I07F001FCI01EK01F8,7F8J03FE0FF801FEI01FFK07FCL03FC0FFK03FC0FF003LFL0EK01EL01E007J01EJ03CL03CI01FCK038I01KFJ01EL0F8,FFK01FE0FF801FEI01FFK07FCL03FC0FFK03FC0FF003LFL0EK01CL01E00FK0FJ038L03CJ0F8K038J0JFEJ01EL0F8,7FK01FE07F001FEJ0FFK03F8L01F80FEK03FC0FF001KFEL0EK03CM0E007K0FJ078L03CJ0F8K038J01IFK01EL078,jT04gR01E,^FS`;
  } else {
    return "";
  }
};
